import React from 'react'
import get from 'lodash/get'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import CelebrationOutlinedIcon from 'components/material-symbol/icons/Celebration'
import { MY_PGA_COM_HOST } from 'env'
import Dialog from 'components/dialog'
import BookingUrlField from 'components/share-box/booking-url-field'
import BookingShareActions from 'components/share-box/booking-share-actions'
import ShareBookingUrlAndAccessCodeSection from './share-booking-url-and-access-code-section'
import CircularHighlightedIcon from 'components/circular-highlighted-icon'
import { useAuth } from 'lib/auth'
import { useHistory } from 'react-router-dom'
import { OnboardingSuccessDialog } from 'components/group-events/create-group-event-form-wizard/steps/success'
import { PROMO_CALLOUT_BORDER_RADIUS } from 'lib/form-field-styles'

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
  },
  dialogTitle: {
    padding: theme.spacing(4),
    paddingRight: theme.spacing(7),
    paddingBottom: 0,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  dialogContent: {
    padding: theme.spacing(4),
    paddingTop: theme.spacing(2),
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(0, 4, 4),
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 3, 3),
    },
  },
  primaryAction: {
    width: '100%',
  },
}))

const formatBookingTypeUrl = lessonType => {
  if (!lessonType) {
    return null
  }

  const coachId = lessonType?.coach?.externalId
  const coachSlug = get(lessonType, 'coach.coachProfile.slug')
  const lessonTypeId = lessonType.id
  const locationId = get(lessonType, 'facilities[0].id')
  const isLessonPackOffering = lessonType?.lessonPackOfferings?.length > 0

  let bookingUrl = `${MY_PGA_COM_HOST}/coach/${coachSlug ||
    coachId}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}`

  if (isLessonPackOffering) {
    bookingUrl += '&isLessonPacks=true'
  }

  return bookingUrl
}

const BookingTypeFormSuccessDialog = ({
  isOpen,
  setIsOpen,
  onClose,
  onCreateAnother,
  lessonType,
  showOnboardingModal,
}) => {
  const classes = useStyles()
  const { user } = useAuth()
  const history = useHistory()
  const bookingTypeUrl = formatBookingTypeUrl(lessonType)
  const accessCode = lessonType?.accessCode
  const shareDescription = accessCode
    ? 'Share the link below with your students so they can register for this offering. Include the access code if you added one.'
    : 'Share your schedule link below with your students so they can book this offering.'

  const handleClose = () => {
    setIsOpen(false)
    onClose()
  }

  const handleFinished = () => {
    handleClose()
    if (user.onboarding) {
      history.push('/pga-coach/set-up-schedule')
    }
  }

  return showOnboardingModal ? (
    <OnboardingSuccessDialog isOpen={isOpen} setIsOpen={setIsOpen} />
  ) : (
    <Dialog
      isOpen={isOpen}
      maxWidth="sm"
      fullWidth
      showCloseButton
      onClose={handleClose}
      classes={{ paper: classes.paper }}
    >
      {() => (
        <>
          <Box className={classes.iconWrapper} pt={4} px={4} pb={0}>
            <CircularHighlightedIcon
              icon={CelebrationOutlinedIcon}
              size={64}
              iconSize={28}
            />
          </Box>
          <DialogTitle className={classes.dialogTitle} disableTypography>
            <Typography className={classes.title} component="h1">
              Great work!
            </Typography>
            <Typography className={classes.subtitle}>
              You just created a new booking type. {shareDescription}
            </Typography>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {accessCode ? (
              <ShareBookingUrlAndAccessCodeSection
                url={bookingTypeUrl}
                accessCode={accessCode}
                title={lessonType?.title}
              />
            ) : (
              bookingTypeUrl && (
                <>
                  <BookingUrlField text={bookingTypeUrl} />
                  <BookingShareActions
                    url={bookingTypeUrl}
                    title={lessonType?.title}
                    onText={() =>
                      history.push(
                        `/pga-coach/messages?shared=${encodeURIComponent(
                          bookingTypeUrl,
                        )}&att=pga-lesson-type&id=${lessonType?.id}`,
                      )
                    }
                  />
                </>
              )
            )}
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryAction}
              onClick={onCreateAnother}
            >
              Create Another Booking Type
            </Button>
            <Button color="primary" onClick={handleFinished}>
              I&apos;m Finished
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default BookingTypeFormSuccessDialog
